import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Search,
  X,
  Command,
  CornerDownLeft,
  ArrowUp,
  ArrowDown,
  User,
  BookOpen,
  Calendar,
  Settings,
  MessageSquare,
  DollarSign,
  GraduationCap,
  Users,
  Video,
  FileText,
  Home,
  Building2,
  Clock,
  ChevronRight,
} from 'lucide-react';

// Result types
type ResultCategory = 'navigation' | 'users' | 'courses' | 'recent' | 'actions';

interface SearchResult {
  id: string;
  category: ResultCategory;
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  action: () => void;
  keywords?: string[];
}

interface SpotlightSearchProps {
  isOpen: boolean;
  onClose: () => void;
  platform?: 'admin' | 'teacher' | 'student' | 'super-admin';
}

// Icon map for consistent sizing
const iconSize = 18;

// Platform-specific navigation items
const getNavigationItems = (platform: string, navigate: (path: string) => void): SearchResult[] => {
  const items: Record<string, SearchResult[]> = {
    admin: [
      { id: 'nav-dashboard', category: 'navigation', title: 'Dashboard', subtitle: 'Go to main dashboard', icon: <Home size={iconSize} />, action: () => navigate('/admin'), keywords: ['home', 'main'] },
      { id: 'nav-academics', category: 'navigation', title: 'Academics', subtitle: 'View academic overview', icon: <GraduationCap size={iconSize} />, action: () => navigate('/admin/academics'), keywords: ['school', 'education'] },
      { id: 'nav-courses', category: 'navigation', title: 'Courses', subtitle: 'Manage courses', icon: <BookOpen size={iconSize} />, action: () => navigate('/admin/courses'), keywords: ['class', 'lesson'] },
      { id: 'nav-meetings', category: 'navigation', title: 'Meetings', subtitle: 'View scheduled meetings', icon: <Video size={iconSize} />, action: () => navigate('/admin/meetings'), keywords: ['video', 'call'] },
      { id: 'nav-messages', category: 'navigation', title: 'Messages', subtitle: 'View messages', icon: <MessageSquare size={iconSize} />, action: () => navigate('/admin/messages'), keywords: ['chat', 'inbox'] },
      { id: 'nav-finance', category: 'navigation', title: 'Finance', subtitle: 'Financial overview', icon: <DollarSign size={iconSize} />, action: () => navigate('/admin/finance'), keywords: ['money', 'payment', 'billing'] },
      { id: 'nav-settings', category: 'navigation', title: 'Settings', subtitle: 'Configure settings', icon: <Settings size={iconSize} />, action: () => navigate('/admin/settings'), keywords: ['preferences', 'config'] },
      { id: 'nav-profile', category: 'navigation', title: 'My Profile', subtitle: 'View your profile', icon: <User size={iconSize} />, action: () => navigate('/admin/profile'), keywords: ['account'] },
      { id: 'nav-school', category: 'navigation', title: 'My School', subtitle: 'School settings', icon: <Building2 size={iconSize} />, action: () => navigate('/admin/school'), keywords: ['organization'] },
    ],
    teacher: [
      { id: 'nav-dashboard', category: 'navigation', title: 'Dashboard', subtitle: 'Go to main dashboard', icon: <Home size={iconSize} />, action: () => navigate('/teacher'), keywords: ['home', 'main'] },
      { id: 'nav-courses', category: 'navigation', title: 'My Courses', subtitle: 'View your courses', icon: <BookOpen size={iconSize} />, action: () => navigate('/teacher/courses'), keywords: ['class', 'lesson'] },
      { id: 'nav-schedule', category: 'navigation', title: 'My Schedule', subtitle: 'View calendar', icon: <Calendar size={iconSize} />, action: () => navigate('/teacher/schedule'), keywords: ['calendar', 'events'] },
      { id: 'nav-library', category: 'navigation', title: 'My Library', subtitle: 'Teaching materials', icon: <FileText size={iconSize} />, action: () => navigate('/teacher/library'), keywords: ['files', 'resources'] },
      { id: 'nav-finance', category: 'navigation', title: 'Finance', subtitle: 'View earnings', icon: <DollarSign size={iconSize} />, action: () => navigate('/teacher/finance'), keywords: ['salary', 'payment'] },
      { id: 'nav-messages', category: 'navigation', title: 'Messages', subtitle: 'View messages', icon: <MessageSquare size={iconSize} />, action: () => navigate('/teacher/messages'), keywords: ['chat', 'inbox'] },
      { id: 'nav-profile', category: 'navigation', title: 'My Profile', subtitle: 'View your profile', icon: <User size={iconSize} />, action: () => navigate('/teacher/profile'), keywords: ['account'] },
    ],
    student: [
      { id: 'nav-dashboard', category: 'navigation', title: 'Dashboard', subtitle: 'Go to main dashboard', icon: <Home size={iconSize} />, action: () => navigate('/student'), keywords: ['home', 'main'] },
      { id: 'nav-courses', category: 'navigation', title: 'My Courses', subtitle: 'View enrolled courses', icon: <BookOpen size={iconSize} />, action: () => navigate('/student/courses'), keywords: ['class', 'lesson'] },
      { id: 'nav-schedule', category: 'navigation', title: 'My Schedule', subtitle: 'View calendar', icon: <Calendar size={iconSize} />, action: () => navigate('/student/schedule'), keywords: ['calendar', 'events'] },
      { id: 'nav-tasks', category: 'navigation', title: 'Tasks & Exams', subtitle: 'View assignments', icon: <FileText size={iconSize} />, action: () => navigate('/student/tasks'), keywords: ['homework', 'assignments'] },
      { id: 'nav-messages', category: 'navigation', title: 'Messages', subtitle: 'View messages', icon: <MessageSquare size={iconSize} />, action: () => navigate('/student/messages'), keywords: ['chat', 'inbox'] },
      { id: 'nav-profile', category: 'navigation', title: 'My Profile', subtitle: 'View your profile', icon: <User size={iconSize} />, action: () => navigate('/student/profile'), keywords: ['account'] },
    ],
    'super-admin': [
      { id: 'nav-dashboard', category: 'navigation', title: 'Dashboard', subtitle: 'Platform overview', icon: <Home size={iconSize} />, action: () => navigate('/super-admin'), keywords: ['home', 'main'] },
      { id: 'nav-schools', category: 'navigation', title: 'Schools', subtitle: 'Manage schools', icon: <Building2 size={iconSize} />, action: () => navigate('/super-admin/schools'), keywords: ['organization'] },
      { id: 'nav-users', category: 'navigation', title: 'Users', subtitle: 'Manage all users', icon: <Users size={iconSize} />, action: () => navigate('/super-admin/users'), keywords: ['accounts'] },
      { id: 'nav-subscriptions', category: 'navigation', title: 'Subscriptions', subtitle: 'Manage plans', icon: <DollarSign size={iconSize} />, action: () => navigate('/super-admin/subscriptions'), keywords: ['plans', 'billing'] },
      { id: 'nav-storage', category: 'navigation', title: 'Storage', subtitle: 'Storage management', icon: <FileText size={iconSize} />, action: () => navigate('/super-admin/storage'), keywords: ['disk', 'files'] },
      { id: 'nav-audit', category: 'navigation', title: 'Audit Logs', subtitle: 'View activity logs', icon: <Clock size={iconSize} />, action: () => navigate('/super-admin/audit-logs'), keywords: ['history', 'activity'] },
      { id: 'nav-admins', category: 'navigation', title: 'Super Admins', subtitle: 'Manage admins', icon: <User size={iconSize} />, action: () => navigate('/super-admin/admins'), keywords: ['administrators'] },
      { id: 'nav-settings', category: 'navigation', title: 'Settings', subtitle: 'Platform settings', icon: <Settings size={iconSize} />, action: () => navigate('/super-admin/settings'), keywords: ['config'] },
    ],
  };

  return items[platform] || items.admin;
};

// Mock recent searches
const getRecentSearches = (): SearchResult[] => {
  return [
    { id: 'recent-1', category: 'recent', title: 'Math 101 Course', subtitle: 'Viewed 2 hours ago', icon: <Clock size={iconSize} className="text-gray-400" />, action: () => {}, keywords: [] },
    { id: 'recent-2', category: 'recent', title: 'David Cohen', subtitle: 'Student profile', icon: <Clock size={iconSize} className="text-gray-400" />, action: () => {}, keywords: [] },
  ];
};

// Quick actions
const getQuickActions = (navigate: (path: string) => void): SearchResult[] => {
  return [
    { id: 'action-create-course', category: 'actions', title: 'Create New Course', subtitle: 'Quick action', icon: <BookOpen size={iconSize} className="text-purple-600" />, action: () => navigate('/admin/courses?action=create'), keywords: ['new', 'add'] },
    { id: 'action-schedule-meeting', category: 'actions', title: 'Schedule Meeting', subtitle: 'Quick action', icon: <Video size={iconSize} className="text-purple-600" />, action: () => navigate('/admin/meetings?action=create'), keywords: ['new', 'add'] },
    { id: 'action-send-message', category: 'actions', title: 'Send Message', subtitle: 'Quick action', icon: <MessageSquare size={iconSize} className="text-purple-600" />, action: () => navigate('/admin/messages?action=compose'), keywords: ['new', 'write'] },
  ];
};

export function SpotlightSearch({ isOpen, onClose, platform = 'admin' }: SpotlightSearchProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Navigation function that also closes spotlight
  const handleNavigate = useCallback((path: string) => {
    navigate(path);
    onClose();
  }, [navigate, onClose]);

  // Get all searchable items
  const allItems = useMemo(() => {
    return [
      ...getNavigationItems(platform, handleNavigate),
      ...getQuickActions(handleNavigate),
      ...getRecentSearches(),
    ];
  }, [platform, handleNavigate]);

  // Filter results based on query
  const filteredResults = useMemo(() => {
    if (!query.trim()) {
      // Show navigation and recent when no query
      const nav = allItems.filter((item) => item.category === 'navigation').slice(0, 5);
      const recent = allItems.filter((item) => item.category === 'recent');
      return [...nav, ...recent];
    }

    const lowerQuery = query.toLowerCase();
    return allItems.filter((item) => {
      const titleMatch = item.title.toLowerCase().includes(lowerQuery);
      const subtitleMatch = item.subtitle?.toLowerCase().includes(lowerQuery);
      const keywordMatch = item.keywords?.some((k) => k.toLowerCase().includes(lowerQuery));
      return titleMatch || subtitleMatch || keywordMatch;
    }).slice(0, 10);
  }, [query, allItems]);

  // Group results by category
  const groupedResults = useMemo(() => {
    const groups: Record<ResultCategory, SearchResult[]> = {
      navigation: [],
      users: [],
      courses: [],
      recent: [],
      actions: [],
    };

    filteredResults.forEach((result) => {
      groups[result.category].push(result);
    });

    return groups;
  }, [filteredResults]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, filteredResults.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredResults[selectedIndex]) {
          filteredResults[selectedIndex].action();
          onClose();
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  }, [filteredResults, selectedIndex, onClose]);

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
    if (!isOpen) {
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Scroll selected item into view
  useEffect(() => {
    const selectedElement = resultsRef.current?.querySelector(`[data-index="${selectedIndex}"]`);
    selectedElement?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  // Global keyboard shortcut
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K to open
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (isOpen) {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const categoryLabels: Record<ResultCategory, string> = {
    navigation: 'Navigation',
    users: 'Users',
    courses: 'Courses',
    recent: 'Recent',
    actions: 'Quick Actions',
  };

  let flatIndex = 0;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50 animate-fade-in"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed left-1/2 top-[20%] -translate-x-1/2 w-full max-w-xl z-50 animate-fade-in">
        <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
          {/* Search Input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
            <Search size={20} className="text-gray-400 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search or jump to..."
              className="flex-1 bg-transparent text-lg outline-none placeholder-gray-400"
              autoComplete="off"
            />
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-500 font-mono">esc</kbd>
              <span>to close</span>
            </div>
          </div>

          {/* Results */}
          <div ref={resultsRef} className="max-h-[400px] overflow-y-auto">
            {filteredResults.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Search size={32} className="mx-auto text-gray-300 mb-2" />
                <p>No results found for "{query}"</p>
                <p className="text-sm mt-1">Try searching for pages, users, or courses</p>
              </div>
            ) : (
              Object.entries(groupedResults).map(([category, items]) => {
                if (items.length === 0) return null;

                return (
                  <div key={category}>
                    <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase bg-gray-50 sticky top-0">
                      {categoryLabels[category as ResultCategory]}
                    </div>
                    {items.map((result) => {
                      const currentIndex = flatIndex++;
                      const isSelected = currentIndex === selectedIndex;

                      return (
                        <button
                          key={result.id}
                          data-index={currentIndex}
                          onClick={() => {
                            result.action();
                            onClose();
                          }}
                          onMouseEnter={() => setSelectedIndex(currentIndex)}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                            isSelected ? 'bg-purple-50' : 'hover:bg-gray-50'
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            isSelected ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {result.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium truncate ${isSelected ? 'text-purple-900' : 'text-gray-900'}`}>
                              {result.title}
                            </p>
                            {result.subtitle && (
                              <p className="text-sm text-gray-500 truncate">{result.subtitle}</p>
                            )}
                          </div>
                          {isSelected && (
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <CornerDownLeft size={14} className="text-purple-400" />
                              <span className="text-xs text-purple-400">Enter</span>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-2 border-t border-gray-200 bg-gray-50 text-xs text-gray-500">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-gray-200 rounded font-mono"><ArrowUp size={10} /></kbd>
                <kbd className="px-1 py-0.5 bg-gray-200 rounded font-mono"><ArrowDown size={10} /></kbd>
                <span>to navigate</span>
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-gray-200 rounded font-mono">Enter</kbd>
                <span>to select</span>
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Command size={12} />
              <span>K to toggle</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// Hook to manage spotlight state
export function useSpotlight() {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  // Global keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        toggle();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggle]);

  return { isOpen, open, close, toggle };
}

export default SpotlightSearch;
